/**
 * Notification Trigger Service
 * 
 * Provides helper functions to trigger notifications for various events
 * This service wraps the notification creation logic and sends push notifications
 */

import { NotificationServiceV2 } from './NotificationServiceV2';
import { NotificationRoutingService } from './NotificationRoutingService';
import { StoreService } from './StoreService';
import { userService } from './UserService';
import { ENV } from '../config/env';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase/client';
import { UserManagementService } from './UserManagementService';

// Firebase Functions URL - will be set from environment or use default
const getFunctionsUrl = (functionName: string): string => {
  const functionsUrl = ENV.FIREBASE_FUNCTIONS_URL;
  if (functionsUrl) {
    return `${functionsUrl}/${functionName}`;
  }
  // Default Firebase Functions URL format for v2 functions
  const projectId = ENV.FIREBASE_PROJECT_ID || 'mckenzieportal-41c3d';
  const normalizedName = functionName.toLowerCase();
  return `https://${normalizedName}-nerqonhija-uc.a.run.app`;
};

/**
 * Send push notification via Firebase Functions
 * This is a non-blocking operation - failures are logged but don't throw
 */
async function sendPushNotificationViaFunction(
  userId: string,
  title: string,
  body: string,
  data?: any
): Promise<void> {
  // Skip push notifications in development if CORS is causing issues or function is not deployed
  // This can be controlled via environment variable
  const disablePushInDev = import.meta.env.VITE_DISABLE_PUSH_IN_DEV === 'true';
  const isDevelopment = import.meta.env.DEV || window.location.hostname === 'localhost';
  
  if (disablePushInDev && isDevelopment) {
    console.log('📱 Push notifications disabled in development (via VITE_DISABLE_PUSH_IN_DEV)');
    return;
  }
  
  try {
    // In development, if using the default URL (no custom functions URL configured), skip silently
    // This prevents 404 errors in console during development when the function may not be deployed
    const hasCustomFunctionsUrl = !!ENV.FIREBASE_FUNCTIONS_URL;
    
    if (isDevelopment && !hasCustomFunctionsUrl) {
      // Skip push notifications in development when no custom function URL is configured
      // This prevents 404 errors in console during development when the function may not be deployed
      // Set VITE_FIREBASE_FUNCTIONS_URL environment variable to enable push notifications in development
      return; // Skip silently to avoid console noise
    }
    
    const functionUrl = getFunctionsUrl('sendPushNotification');
    
    // Use a timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors', // Explicitly set CORS mode
        credentials: 'omit', // Don't send credentials for CORS
        body: JSON.stringify({
          userId,
          title,
          body,
          data: {
            ...data,
            notificationId: data?.notificationId || Date.now().toString()
          },
          priority: data?.priority || 'normal'
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Handle 404 specifically - function may not be deployed or URL is incorrect
        if (response.status === 404) {
          console.warn(`⚠️ Push notification function not found (404) - endpoint may not be deployed: ${functionUrl}`);
          console.warn('⚠️ Push notifications will be skipped. This is non-blocking and in-app notifications will still work.');
          return; // Exit early - don't throw for 404
        }
        
        // For other errors, try to get error message but don't fail hard
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const result = await response.json().catch(() => ({}));
      console.log(`✅ Push notification sent to user ${userId}`, result);
    } catch (fetchError: any) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        console.warn('⚠️ Push notification request timed out');
      } else if (fetchError.message?.includes('CORS') || fetchError.message?.includes('Failed to fetch')) {
        // CORS errors are expected in some environments but shouldn't fail silently
        console.warn('⚠️ Push notification CORS error - this may be a configuration issue:', fetchError.message);
        console.warn('⚠️ Ensure the cloud function has CORS enabled and proper headers');
        // Don't throw - continue gracefully
      } else if (fetchError.message?.includes('404')) {
        // Already handled above, but catch here as well for safety
        console.warn('⚠️ Push notification function not found (404) - skipping push notification');
      } else {
        // For other errors, log but don't throw
        console.warn('⚠️ Push notification error:', fetchError.message || fetchError);
      }
    }
  } catch (error: any) {
    // Log error but don't throw - push notifications are optional and shouldn't block main operations
    console.warn('⚠️ Failed to send push notification (non-blocking):', error.message || error);
    // Don't throw - push notification failure shouldn't break in-app notifications
  }
}

export class NotificationTriggerService {
  /**
   * Trigger notification when a note is added to a store
   */
  static async triggerNoteAddedNotification(
    storeId: string,
    noteId: string,
    noteCreatedByUserId: string,
    storeName?: string
  ): Promise<void> {
    try {
      // Get store data (name and customer number)
      let finalStoreName = storeName;
      let customerNo: string | number | undefined;
      let store;
      
      try {
        store = await StoreService.getStoreById(storeId);
        if (store) {
          finalStoreName = finalStoreName || store.name || store.StoreName || `Store ${storeId}`;
          customerNo = store.CustomerNo || store.customerNo || undefined;
          console.log('Store data fetched:', { finalStoreName, customerNo, storeId });
        }
      } catch (error) {
        console.warn('Error fetching store data:', error);
        finalStoreName = finalStoreName || `Store ${storeId}`;
      }

      // Fetch note text from Firestore
      let noteText = '';
      try {
        const noteDocRef = doc(db, 'Stores', storeId, 'notes', noteId);
        const noteDocSnap = await getDoc(noteDocRef);
        if (noteDocSnap.exists()) {
          const noteData = noteDocSnap.data();
          noteText = noteData.text || noteData.note || '';
        }
      } catch (error) {
        console.warn('Could not fetch note text:', error);
      }

      // Get creator user info with first/last name
      // noteCreatedByUserId could be either user ID (Firestore doc ID) or email
      let creator;
      let creatorUserId = noteCreatedByUserId;
      let creatorFirstName = '';
      let creatorLastName = '';
      
      // First, try to get user by ID (if it's a Firestore document ID)
      try {
        creator = await userService.getUserById(noteCreatedByUserId);
        if (creator) {
          creatorUserId = creator.id;
        }
      } catch (error) {
        // Not a valid ID, continue to try email lookup
      }
      
      // If not found by ID, try to find by email or in all users
      if (!creator) {
        try {
          // Check if it looks like an email
          if (noteCreatedByUserId.includes('@')) {
            creator = await userService.getUserByEmail(noteCreatedByUserId);
            if (creator) {
              creatorUserId = creator.id;
            }
          } else {
            // Try to find in all users by ID or email
            const allUsers = await userService.getAllUsers();
            creator = allUsers.find(u => 
              u.id === noteCreatedByUserId || 
              u.email === noteCreatedByUserId ||
              u.email === noteCreatedByUserId.toLowerCase()
            );
            if (creator) {
              creatorUserId = creator.id;
            }
          }
        } catch (error) {
          console.warn('Could not find user:', error);
        }
      }
      
      // Try to get full user profile with first/last name from UserManagementService
      if (creatorUserId) {
        try {
          const userProfile = await UserManagementService.getUserById(creatorUserId);
          if (userProfile) {
            creatorFirstName = userProfile.firstName || '';
            creatorLastName = userProfile.lastName || '';
            console.log('User profile fetched:', { creatorFirstName, creatorLastName, creatorUserId });
          }
        } catch (error) {
          console.warn('Could not fetch user profile:', error);
          // Try to extract from username if available
          if (creator?.username) {
            const usernameParts = creator.username.split('.');
            if (usernameParts.length >= 2) {
              creatorFirstName = usernameParts[0] || '';
              creatorLastName = usernameParts[1] || '';
            }
          }
        }
      }
      
      const creatorName = creator?.username || creator?.email || noteCreatedByUserId || 'Someone';
      const creatorFullName = creatorFirstName && creatorLastName 
        ? `${creatorFirstName} ${creatorLastName}`.trim()
        : creatorName;

      // Track note submission history using the actual user ID
      if (creatorUserId && creator) {
        try {
          await NotificationServiceV2.trackNoteSubmission(creatorUserId, storeId, noteId);
        } catch (error) {
          console.warn('Could not track note submission:', error);
        }
      }

      // Get users to notify - use the actual user ID if we found it, otherwise use original
      const usersToNotify = await NotificationRoutingService.getUsersToNotifyForNote(
        storeId,
        creatorUserId || noteCreatedByUserId
      );

      // Create notifications for each user
      for (const userId of usersToNotify) {
        // Build metadata object, filtering out undefined values (Firestore doesn't allow undefined)
        const metadata: any = {
          storeId,
          noteId,
          link: `/store-details/${storeId}`,
        };
        
        if (customerNo) {
          metadata.customerNo = String(customerNo);
        }
        if (finalStoreName) {
          metadata.storeName = finalStoreName;
        }
        if (noteText) {
          metadata.noteText = noteText;
        }
        if (creatorFirstName) {
          metadata.creatorFirstName = creatorFirstName;
        }
        if (creatorLastName) {
          metadata.creatorLastName = creatorLastName;
        }
        if (creatorFullName) {
          metadata.creatorFullName = creatorFullName;
        }
        
        const notificationId = await NotificationServiceV2.createNotification({
          userId,
          type: 'NOTE_ADDED',
          title: 'New Note Added',
          message: `${creatorName} added a note to ${finalStoreName}`,
          priority: 'MEDIUM',
          metadata
        });

        // Send push notification
        await sendPushNotificationViaFunction(
          userId,
          'New Note Added',
          `${creatorName} added a note to ${finalStoreName}`,
          {
            notificationId,
            type: 'NOTE_ADDED',
            storeId,
            noteId,
            deepLink: `/store-details/${storeId}`,
            priority: 'normal'
          }
        );
      }

      console.log(`✅ Created ${usersToNotify.length} notifications for note added to store ${storeId}`);
    } catch (error: any) {
      console.error('Error triggering note added notification:', error);
      // Don't throw - notification failure shouldn't break note creation
    }
  }

  /**
   * Trigger notification when a reply is added to a note
   */
  static async triggerNoteReplyNotification(
    storeId: string,
    noteId: string,
    replyCreatedByUserId: string,
    originalNoteAuthor: string, // Email of the original note author
    storeName?: string
  ): Promise<void> {
    try {
      // Get store name if not provided
      let finalStoreName = storeName;
      if (!finalStoreName) {
        const store = await StoreService.getStoreById(storeId);
        finalStoreName = store?.name || `Store ${storeId}`;
      }

      // Find the original note author's user ID
      let originalAuthorUser;
      try {
        originalAuthorUser = await userService.getUserByEmail(originalNoteAuthor);
      } catch {
        // If lookup by email fails, try to get from all users
        const allUsers = await userService.getAllUsers();
        originalAuthorUser = allUsers.find(u => u.email === originalNoteAuthor);
      }

      // Don't notify if the reply author is the same as the original note author
      let replyAuthorUserId = replyCreatedByUserId;
      try {
        const allUsers = await userService.getAllUsers();
        const replyAuthor = allUsers.find(u => u.id === replyCreatedByUserId || u.email === replyCreatedByUserId);
        if (replyAuthor) {
          replyAuthorUserId = replyAuthor.id;
        }
      } catch (error) {
        console.warn('Could not find reply author:', error);
      }

      if (!originalAuthorUser) {
        console.warn(`Original note author ${originalNoteAuthor} not found, skipping notification`);
        return;
      }

      // Don't notify if replying to own note
      if (originalAuthorUser.id === replyAuthorUserId) {
        console.log('Reply author is same as note author, skipping notification');
        return;
      }

      // Get reply author name
      let replyAuthorName;
      try {
        const allUsers = await userService.getAllUsers();
        const replyAuthor = allUsers.find(u => u.id === replyAuthorUserId || u.email === replyCreatedByUserId);
        replyAuthorName = replyAuthor?.username || replyAuthor?.email || replyCreatedByUserId || 'Someone';
      } catch {
        replyAuthorName = replyCreatedByUserId || 'Someone';
      }

      // Notify the original note author
      await NotificationServiceV2.createNotification({
        userId: originalAuthorUser.id,
        type: 'NOTE_ADDED',
        title: 'New Reply to Your Note',
        message: `${replyAuthorName} replied to your note on ${finalStoreName}`,
        priority: 'MEDIUM',
        metadata: {
          storeId,
          noteId,
          link: `/store-details/${storeId}`
        }
      });

      // Send push notification
      await sendPushNotificationViaFunction(
        originalAuthorUser.id,
        'New Reply to Your Note',
        `${replyAuthorName} replied to your note on ${finalStoreName}`,
        {
          notificationId: Date.now().toString(),
          type: 'NOTE_ADDED',
          storeId,
          noteId,
          deepLink: `/store-details/${storeId}`,
          priority: 'normal'
        }
      );

      console.log(`✅ Created notification for reply to note at store ${storeId}`);
    } catch (error: any) {
      console.error('Error triggering note reply notification:', error);
      // Don't throw - notification failure shouldn't break reply creation
    }
  }

  /**
   * Trigger notification when an order is submitted
   */
  static async triggerOrderSubmittedNotification(
    storeId: string,
    orderId: string,
    storeName?: string
  ): Promise<void> {
    try {
      // Get store name if not provided
      let finalStoreName = storeName;
      if (!finalStoreName) {
        const store = await StoreService.getStoreById(storeId);
        finalStoreName = store?.name || `Store ${storeId}`;
      }

      // Get users to notify (only managers)
      const usersToNotify = await NotificationRoutingService.getUsersToNotifyForOrder(storeId);

      // Create notifications for each manager
      for (const userId of usersToNotify) {
        const notificationId = await NotificationServiceV2.createNotification({
          userId,
          type: 'ORDER_SUBMITTED',
          title: 'New Order Submitted',
          message: `New order submitted for ${finalStoreName}`,
          priority: 'HIGH',
          metadata: {
            storeId,
            orderId,
            link: `/manager/order-details/${storeId}/${orderId}`
          }
        });

        // Send push notification
        await sendPushNotificationViaFunction(
          userId,
          'New Order Submitted',
          `New order submitted for ${finalStoreName}`,
          {
            notificationId,
            type: 'ORDER_SUBMITTED',
            storeId,
            orderId,
            deepLink: `/manager/order-details/${storeId}/${orderId}`,
            priority: 'high'
          }
        );
      }

      console.log(`✅ Created ${usersToNotify.length} notifications for order submitted at store ${storeId}`);
    } catch (error: any) {
      console.error('Error triggering order submitted notification:', error);
      // Don't throw - notification failure shouldn't break order submission
    }
  }

  /**
   * Trigger notification when a resource is added
   */
  static async triggerResourceAddedNotification(
    resourceId: string,
    resourceTitle: string,
    resourceType?: string
  ): Promise<void> {
    try {
      // Get all users to notify
      const usersToNotify = await NotificationRoutingService.getUsersToNotifyForResource();

      // Create notifications for each user
      for (const userId of usersToNotify) {
        const notificationId = await NotificationServiceV2.createNotification({
          userId,
          type: 'RESOURCE_ADDED',
          title: 'New Resource Available',
          message: `New resource: ${resourceTitle}${resourceType ? ` (${resourceType})` : ''}`,
          priority: 'MEDIUM',
          metadata: {
            resourceId
            // Don't set link here - let the NotificationCenter handle route based on user role
          }
        });

        // Send push notification
        await sendPushNotificationViaFunction(
          userId,
          'New Resource Available',
          `New resource: ${resourceTitle}${resourceType ? ` (${resourceType})` : ''}`,
          {
            notificationId,
            type: 'RESOURCE_ADDED',
            resourceId,
            deepLink: `/resources/${resourceId}`, // Will be adjusted by NotificationCenter based on role
            priority: 'normal'
          }
        );
      }

      console.log(`✅ Created ${usersToNotify.length} notifications for resource added: ${resourceTitle}`);
    } catch (error: any) {
      console.error('Error triggering resource added notification:', error);
      // Don't throw - notification failure shouldn't break resource creation
    }
  }

  /**
   * Trigger notification when an announcement is created
   */
  static async triggerAnnouncementCreatedNotification(
    announcementId: string,
    announcementTitle: string,
    targetUserIds: string[]
  ): Promise<void> {
    try {
      // Create notifications for each target user
      for (const userId of targetUserIds) {
        const notificationId = await NotificationServiceV2.createNotification({
          userId,
          type: 'ANNOUNCEMENT_CREATED',
          title: 'New Announcement',
          message: announcementTitle,
          priority: 'MEDIUM',
          metadata: {
            announcementId,
            link: `/announcements/${announcementId}`
          }
        });

        // Send push notification
        await sendPushNotificationViaFunction(
          userId,
          'New Announcement',
          announcementTitle,
          {
            notificationId,
            type: 'ANNOUNCEMENT_CREATED',
            announcementId,
            deepLink: `/announcements/${announcementId}`,
            priority: 'normal'
          }
        );
      }

      console.log(`✅ Created ${targetUserIds.length} notifications for announcement ${announcementId}`);
    } catch (error: any) {
      console.error('Error triggering announcement notification:', error);
      // Don't throw - notification failure shouldn't break announcement creation
    }
  }
}

